import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TaxSummaryCard from '../components/TaxSummaryCard';
import GSTReportModal from '../components/GSTReportModal';
import TaxCalculator from '../components/TaxCalculator';
import { useTaxSummary, useTaxLiability, useCompanyInfo } from '../hooks/useTax';

const { 
  FiFileText, FiCalculator, FiCalendar, FiTrendingUp, FiAlertTriangle,
  FiDownload, FiRefreshCw, FiSettings, FiMapPin, FiBuilding, FiMail
} = FiIcons;

const TaxManagement = () => {
  const [selectedPeriod, setSelectedPeriod] = useState({
    start: format(startOfMonth(subMonths(new Date(), 0)), 'yyyy-MM-dd'),
    end: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const { summary, loading: summaryLoading, error } = useTaxSummary(
    selectedPeriod.start, 
    selectedPeriod.end
  );
  
  const { liability, loading: liabilityLoading } = useTaxLiability(
    selectedPeriod.start, 
    selectedPeriod.end
  );
  
  const companyInfo = useCompanyInfo();

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePeriodChange = (type) => {
    const now = new Date();
    let start, end;

    switch (type) {
      case 'current_month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'last_month':
        start = startOfMonth(subMonths(now, 1));
        end = endOfMonth(subMonths(now, 1));
        break;
      case 'current_quarter':
        const currentQuarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), currentQuarter * 3, 1);
        end = new Date(now.getFullYear(), currentQuarter * 3 + 3, 0);
        break;
      default:
        return;
    }

    setSelectedPeriod({
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd')
    });
  };

  const openReportModal = (reportType) => {
    setSelectedReportType(reportType);
    setShowReportModal(true);
  };

  const gstReports = [
    {
      type: 'gstr1',
      title: 'GSTR-1',
      description: 'Outward supplies return',
      frequency: 'Monthly',
      dueDate: '11th of next month',
      icon: FiFileText,
      color: 'blue'
    },
    {
      type: 'gstr3b',
      title: 'GSTR-3B',
      description: 'Summary return',
      frequency: 'Monthly',
      dueDate: '20th of next month',
      icon: FiFileText,
      color: 'green'
    },
    {
      type: 'gstr9',
      title: 'GSTR-9',
      description: 'Annual return',
      frequency: 'Yearly',
      dueDate: '31st December',
      icon: FiFileText,
      color: 'purple'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Tax Overview', icon: FiTrendingUp },
    { id: 'reports', label: 'GST Reports', icon: FiFileText },
    { id: 'calculator', label: 'GST Calculator', icon: FiCalculator },
    { id: 'compliance', label: 'Compliance', icon: FiAlertTriangle }
  ];

  if (summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading tax data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Tax Management</h1>
          <p className="text-gray-600">
            GST compliance, calculations, and reporting for your agency
          </p>
        </div>
        
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Period:</label>
            <select
              onChange={(e) => handlePeriodChange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="current_month">Current Month</option>
              <option value="last_month">Last Month</option>
              <option value="current_quarter">Current Quarter</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="date"
              value={selectedPeriod.start}
              onChange={(e) => setSelectedPeriod(prev => ({ ...prev, start: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={selectedPeriod.end}
              onChange={(e) => setSelectedPeriod(prev => ({ ...prev, end: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </motion.div>

      {/* Company Info Card */}
      {companyInfo && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-200"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
                <SafeIcon icon={FiBuilding} className="w-6 h-6 mr-2 text-blue-600" />
                {companyInfo.name}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">GSTIN:</p>
                  <p className="font-semibold font-mono">{companyInfo.gstin}</p>
                </div>
                <div>
                  <p className="text-gray-600">State:</p>
                  <p className="font-semibold">{companyInfo.state_name} ({companyInfo.state_code})</p>
                </div>
                <div>
                  <p className="text-gray-600">PAN:</p>
                  <p className="font-semibold font-mono">{companyInfo.pan}</p>
                </div>
              </div>
            </div>
            <div className="text-right">
              <SafeIcon icon={FiSettings} className="w-8 h-8 text-blue-600" />
            </div>
          </div>
        </motion.div>
      )}

      {/* Navigation Tabs */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-xl shadow-soft border border-gray-200"
      >
        <div className="flex flex-wrap border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 px-6 py-4 font-medium transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-b-2 border-blue-500 text-blue-600 bg-blue-50'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </motion.div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === 'overview' && (
          <>
            {/* Tax Summary Cards */}
            {summary && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
              >
                <TaxSummaryCard
                  title="Total Sales"
                  amount={summary.totals.total_sales}
                  subtitle={`${formatDate(selectedPeriod.start)} - ${formatDate(selectedPeriod.end)}`}
                  icon="sales"
                  color="blue"
                />
                <TaxSummaryCard
                  title="Total GST"
                  amount={summary.totals.total_tax}
                  subtitle={`${summary.totals.b2b_sales > 0 ? 'B2B + B2C' : 'B2C Only'}`}
                  icon="tax"
                  color="green"
                />
                <TaxSummaryCard
                  title="CGST + SGST"
                  amount={summary.totals.total_cgst + summary.totals.total_sgst}
                  subtitle="Intra-state transactions"
                  icon="cgst"
                  color="purple"
                />
                <TaxSummaryCard
                  title="IGST"
                  amount={summary.totals.total_igst}
                  subtitle="Inter-state transactions"
                  icon="igst"
                  color="orange"
                />
              </motion.div>
            )}

            {/* Tax Breakdown */}
            {summary && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By State */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 mr-2 text-blue-600" />
                    Sales by State
                  </h3>
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {Object.values(summary.by_state).map((state, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-800">{state.state_name}</p>
                          <p className="text-xs text-gray-600">Code: {state.state_code}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-800">{formatCurrency(state.total_sales)}</p>
                          <p className="text-xs text-gray-600">Tax: {formatCurrency(state.total_tax)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* By Tax Rate */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <SafeIcon icon={FiCalculator} className="w-5 h-5 mr-2 text-green-600" />
                    Sales by Tax Rate
                  </h3>
                  
                  <div className="space-y-3">
                    {Object.values(summary.by_rate).map((rate, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-800">{rate.rate}% GST</p>
                          <p className="text-xs text-gray-600">
                            {rate.rate === 0 ? 'Exempt/B2C' : 'Taxable'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-gray-800">{formatCurrency(rate.total_sales)}</p>
                          <p className="text-xs text-gray-600">Tax: {formatCurrency(rate.total_tax)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            )}

            {/* Tax Liability */}
            {liability && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-red-50 rounded-xl p-6 border border-red-200"
              >
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-2 text-red-600" />
                  Tax Liability & Due Dates
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600">IGST Liability</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(liability.liability.igst_liability)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600">CGST Liability</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(liability.liability.cgst_liability)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600">SGST Liability</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(liability.liability.sgst_liability)}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <p className="text-sm text-gray-600">Total Due</p>
                    <p className="text-lg font-bold text-red-600">{formatCurrency(liability.liability.total_liability)}</p>
                    <p className="text-xs text-gray-600">Due: {formatDate(liability.payment_due_date)}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </>
        )}

        {activeTab === 'reports' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-xl p-6 shadow-soft border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">GST Returns & Reports</h3>
              <p className="text-gray-600 mb-6">
                Generate and download GST compliance reports for filing with tax authorities.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {gstReports.map((report) => (
                  <motion.div
                    key={report.type}
                    className={`bg-${report.color}-50 rounded-lg p-6 border border-${report.color}-200 cursor-pointer hover:shadow-md transition-all duration-300`}
                    whileHover={{ y: -2 }}
                    onClick={() => openReportModal(report.type)}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <SafeIcon icon={report.icon} className={`w-8 h-8 text-${report.color}-600`} />
                      <span className={`px-2 py-1 bg-${report.color}-100 text-${report.color}-800 rounded text-xs font-medium`}>
                        {report.frequency}
                      </span>
                    </div>
                    
                    <h4 className="text-xl font-bold text-gray-800 mb-2">{report.title}</h4>
                    <p className="text-gray-600 text-sm mb-3">{report.description}</p>
                    
                    <div className="text-xs text-gray-500">
                      <p><strong>Due Date:</strong> {report.dueDate}</p>
                    </div>
                    
                    <button className={`w-full mt-4 bg-${report.color}-600 hover:bg-${report.color}-700 text-white py-2 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2`}>
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>Generate Report</span>
                    </button>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'calculator' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <TaxCalculator />
          </motion.div>
        )}

        {activeTab === 'compliance' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-2 text-orange-600" />
              Compliance Calendar
            </h3>
            
            <div className="space-y-4">
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2">Upcoming Due Dates</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>GSTR-3B (Current Month)</span>
                    <span className="font-semibold">20th of next month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GSTR-1 (Current Month)</span>
                    <span className="font-semibold">11th of next month</span>
                  </div>
                  <div className="flex justify-between">
                    <span>GST Payment</span>
                    <span className="font-semibold">20th of next month</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <h4 className="font-semibold text-green-800 mb-2">Compliance Status</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span>GST Registration</span>
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">Active</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Last Return Filed</span>
                    <span className="font-semibold">GSTR-3B (Dec 2023)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span>Outstanding Liability</span>
                    <span className="font-semibold">{liability ? formatCurrency(liability.liability.total_liability) : '₹0.00'}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Quick Actions</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <button 
                    onClick={() => openReportModal('gstr3b')}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors duration-200"
                  >
                    File GSTR-3B
                  </button>
                  <button 
                    onClick={() => openReportModal('gstr1')}
                    className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded text-sm transition-colors duration-200"
                  >
                    File GSTR-1
                  </button>
                  <button className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm transition-colors duration-200">
                    Pay GST Online
                  </button>
                  <button 
                    onClick={() => setActiveTab('calculator')}
                    className="bg-purple-600 hover:bg-purple-700 text-white py-2 px-4 rounded text-sm transition-colors duration-200"
                  >
                    Calculate Tax
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* GST Report Modal */}
      <GSTReportModal 
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        reportType={selectedReportType}
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error} (Running in demo mode)</p>
        </div>
      )}
    </div>
  );
};

export default TaxManagement;